/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useFlag } from '@reflag/react-sdk';
import clsx from 'clsx';
import { memo, useMemo, useRef, useState } from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    Badge,
    Collection,
    HeadlessTree,
    HeadlessTreeItem,
    HeadlessTreeItemContent,
    type HeadlessTreeProps,
    type Key,
    renderIcon,
    Tooltip,
    treeDropIndicatorClassName,
    useAutoScroll,
} from '@gitbook/spine-react';

import {
    type AnySiteManagementItem,
    flattenGroupedSections,
    flattenSiteStructure,
    getIndexInSiteStructure,
    getSiteTreeItem,
    getSiteSpacesWithCustomHomePage,
    type SiteCapability,
    type SiteManagementStructureItem,
    type FeatureUpgradeAction,
} from '../../../state';
import { DraftBadge } from './DraftBadge';
import {
    SiteExternalLinkActionsButton,
    type SiteExternalLinkActionsButtonProps,
} from './SiteExternalLinkActionsButton';
import { SiteItemAdaptiveContentButton } from './SiteItemAdaptiveContentButton';
import {
    SiteSectionActionsButton,
    type SiteSectionActionsButtonProps,
} from './SiteSectionActionsButton';
import {
    SiteSectionGroupActionsButton,
    type SiteSectionGroupActionsButtonProps,
} from './SiteSectionGroupActionsButton';
import {
    SiteVariantActionsButton,
    type SiteVariantActionsButtonProps,
} from './SiteVariantActionsButton';
import { SiteVariantLanguageButton } from './SiteVariantLanguageButton';
import {
    type UseSiteStructureTreeDragAndDropProps,
    useSiteStructureTreeDragAndDrop,
} from './useSiteStructureTreeDragAndDrop';
import { useSpaceRevisions } from './useSpaceRevisions';

export interface SiteStructureTreeProps
    extends
        Pick<HeadlessTreeProps<AnySiteManagementItem>, 'selectedKeys' | 'onSelectionChange'>,
        Pick<UseSiteStructureTreeDragAndDropProps, 'onReorder'>,
        Pick<
            SiteStructureTreeItemContentProps,
            'onCreateSection' | 'onCreateSectionGroup' | 'onCreateVariant'
        > {
    siteStructure: api.SiteStructure;
    site: api.Site;
    organization: api.Organization;
    sitePermissions: SiteCapability;
    className?: string;
    isDisabled?: boolean;
    expandedKeys: Set<Key>;
    setExpandedKeys: (keys: Set<Key>) => void;
    upgrade: FeatureUpgradeAction | undefined;
    onCreateExternalLink?: (siteExternalLink: api.SiteExternalLink) => void;
}

export function SiteStructureTree(props: SiteStructureTreeProps) {
    const {
        siteStructure,
        site,
        organization,
        sitePermissions,
        selectedKeys,
        onSelectionChange,
        className,
        onReorder,
        isDisabled,
        upgrade,
        expandedKeys,
        setExpandedKeys,
        onCreateSection,
        onCreateSectionGroup,
        onCreateVariant,
        onCreateExternalLink,
    } = props;

    const spacesWithCustomHomePage = useMemo(
        () => getSiteSpacesWithCustomHomePage(siteStructure).map((siteSpace) => siteSpace.space),
        [siteStructure]
    );
    const revisions = useSpaceRevisions(spacesWithCustomHomePage);

    const siteItem = useMemo(() => {
        const siteSections =
            siteStructure.type === 'sections'
                ? flattenGroupedSections(siteStructure.structure)
                : [];
        const siteSectionsWithVariants = siteSections.filter(
            (siteSection) => siteSection.siteSpaces.length > 1
        );
        return getSiteTreeItem(
            { site, structure: siteStructure },
            {
                includeExternalLinks: true,
                expandableSiteSectionIds: [
                    ...siteSectionsWithVariants.map((siteSection) => siteSection.id),
                ],
                revisions,
            }
        );
    }, [revisions, site, siteStructure]);

    const siteItems = useMemo(() => flattenSiteStructure(siteStructure), [siteStructure]);
    const nestedSectionGroupsFlag = useFlag('NESTED_SECTION_GROUPS');

    const sectionsUpgrade = siteStructure.type === 'sections' ? upgrade : undefined;

    const [draggedKeys, setDraggedKeys] = useState<Set<Key>>(new Set());

    const treeRef = useRef<HTMLDivElement>(null);

    const autoScroll = useAutoScroll(treeRef);
    const { dragAndDropHooks } = useSiteStructureTreeDragAndDrop({
        draggedKeys,
        setDraggedKeys,
        expandedKeys,
        setExpandedKeys,
        getItemIndex: (key) => getIndexInSiteStructure(siteStructure, String(key)),
        getItem: (key) => siteItems.find((item) => item.id === key) ?? null,
        organization,
        site,
        siteStructure,
        nestedSectionGroupsEnabled: nestedSectionGroupsFlag.isEnabled,
        checkIsSectionInGroup: (siteSection) =>
            Boolean(siteStructure.type === 'sections' && siteSection.sectionGroup),
        onReorder,
        // Reordering a section-based structure requires sections access, even for variants.
        // Essential sites should remove sections to use a variants-only structure.
        isDisabled: !sitePermissions.canCreateSite || Boolean(sectionsUpgrade),
        onDragMove(e) {
            autoScroll.move(e.x, e.y);
        },
        onDragEnd() {
            autoScroll.stop();
        },
    });

    return (
        <HeadlessTree<AnySiteManagementItem>
            // Temporary workaround for a bug when switching between sites
            key={siteItem.id}
            ref={treeRef}
            items={siteItem.children as SiteManagementStructureItem[]}
            aria-label="Site structure"
            className={clsx(treeDropIndicatorClassName, className)}
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            selectionBehavior="replace"
            dragAndDropHooks={dragAndDropHooks}
            disabledKeys={
                isDisabled && draggedKeys.size === 0 ? siteItems.map((item) => item.id) : []
            }
        >
            {function renderItem(treeItem) {
                const isExpandable = 'children' in treeItem && treeItem.children.length > 0;

                return (
                    <HeadlessTreeItem
                        textValue={treeItem.title}
                        className={clsx(
                            'group my-px cursor-pointer rounded-sm pl-[calc((var(--tree-item-level)-1)*1rem)] hover:bg-(--neutral-3)',
                            // Disabled
                            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-disabled',
                            // Selected
                            'data-[selected]:bg-(--neutral-4)',
                            // Dragging
                            'data-[dragging]:opacity-drag-preview',
                            // Drop target
                            'data-[drop-target]:outline-1 data-[drop-target]:outline-neutral data-[drop-target]:outline-dashed'
                        )}
                    >
                        <SiteStructureTreeItemContent
                            value={treeItem}
                            isExpandable={isExpandable}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            siteStructure={siteStructure}
                            upgrade={upgrade}
                            onCreateSection={onCreateSection}
                            onCreateSectionGroup={onCreateSectionGroup}
                            onCreateVariant={onCreateVariant}
                            onCreateExternalLink={onCreateExternalLink}
                        />
                        {'children' in treeItem ? (
                            <Collection items={treeItem.children}>{renderItem}</Collection>
                        ) : null}
                    </HeadlessTreeItem>
                );
            }}
        </HeadlessTree>
    );
}

interface SiteStructureTreeItemContentProps
    extends
        Omit<SiteSectionActionsButtonProps, 'siteSection'>,
        Omit<SiteSectionGroupActionsButtonProps, 'siteSectionGroup'>,
        Omit<SiteVariantActionsButtonProps, 'siteSpace'>,
        Omit<SiteExternalLinkActionsButtonProps, 'siteExternalLink'> {
    value: AnySiteManagementItem;
    isExpandable: boolean;
    organization: api.Organization;
}

const SiteStructureTreeItemContent = memo(function SiteStructureTreeItemContent(
    props: SiteStructureTreeItemContentProps
) {
    const {
        value,
        isExpandable,
        site,
        sitePermissions,
        siteStructure,
        organization,
        upgrade,
        onCreateSection,
        onCreateSectionGroup,
        onCreateVariant,
        onCreateExternalLink,
    } = props;
    const isDefault = Boolean(
        (value.type === 'site-section' && value.siteSection.default) ||
        (value.type === 'site-space' && value.siteSpace.default)
    );
    const isHidden = Boolean(
        (value.type === 'site-space' && value.siteSpace.hidden) ||
        (value.type === 'site-section' &&
            value.siteSection.siteSpaces.length > 0 &&
            value.siteSection.siteSpaces.every((space) => space.hidden))
    );
    return (
        <HeadlessTreeItemContent>
            <div className="flex items-center gap-2 px-2 py-1.5">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    {isExpandable ? (
                        <AtomButton
                            slot="chevron"
                            className="flex items-center justify-center rounded-sm p-1 focus-visible:focus-ring"
                        >
                            <Icon.ChevronRight className="size-3 transition-transform group-aria-expanded:rotate-90" />
                        </AtomButton>
                    ) : (
                        <span className="w-5" />
                    )}
                    {renderIcon(value.icon, { className: 'size-4 shrink-0 mt-0.5 text-muted' })}
                    <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                        {value.title}
                        {value.type === 'site-section-group' ? (
                            <Badge label="Group" size="xsmall" kind="secondary" />
                        ) : null}
                        {isDraftSiteTreeItem(value) ? <DraftBadge /> : null}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {isHidden ? (
                        <Tooltip label="Hidden from site navigation">
                            <div className="flex shrink-0">
                                <Icon.EyeOff className="size-3 text-muted" />
                            </div>
                        </Tooltip>
                    ) : null}
                    {isDefault ? (
                        <Tooltip
                            label={
                                value.type === 'site-section' || siteStructure.type === 'siteSpaces'
                                    ? 'Home of the site'
                                    : 'Home of the section'
                            }
                        >
                            <div className="flex shrink-0">
                                <Icon.Home className="size-3 text-muted" />
                            </div>
                        </Tooltip>
                    ) : null}
                    {value.type === 'site-external-link' ? (
                        <Tooltip label="External link">
                            <div className="flex shrink-0">
                                <Icon.Link className="size-3 text-muted" />
                            </div>
                        </Tooltip>
                    ) : null}
                    {value.type === 'site-section' && value.siteSection.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteSection}
                            upgrade={upgrade}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-section-group' && value.siteSectionGroup.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteSectionGroup}
                            upgrade={upgrade}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-space' && value.siteSpace.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteSpace}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-external-link' && value.siteExternalLink.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteExternalLink}
                            upgrade={upgrade}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-space' && value.space.language ? (
                        <SiteVariantLanguageButton
                            siteStructure={siteStructure}
                            siteSpace={value.siteSpace}
                        />
                    ) : null}
                    <div className="flex shrink-0 opacity-0 group-hover:opacity-100 empty:hidden has-[[aria-expanded=true]]:opacity-100">
                        {value.type === 'site-section-group' ? (
                            <SiteSectionGroupActionsButton
                                siteSectionGroup={value.siteSectionGroup}
                                organization={organization}
                                site={site}
                                sitePermissions={sitePermissions}
                                size="small"
                                siteStructure={siteStructure}
                                upgrade={upgrade}
                                onCreateSection={onCreateSection}
                                onCreateSectionGroup={onCreateSectionGroup}
                                onCreateExternalLink={onCreateExternalLink}
                            />
                        ) : value.type === 'site-section' ? (
                            <SiteSectionActionsButton
                                siteStructure={siteStructure}
                                siteSection={value.siteSection}
                                organization={organization}
                                site={site}
                                sitePermissions={sitePermissions}
                                size="small"
                                upgrade={upgrade}
                                onCreateVariant={onCreateVariant}
                                onCreateSectionGroup={onCreateSectionGroup}
                            />
                        ) : value.type === 'site-space' ? (
                            <SiteVariantActionsButton
                                siteSpace={value.siteSpace}
                                organization={organization}
                                site={site}
                                siteStructure={siteStructure}
                                sitePermissions={sitePermissions}
                                size="small"
                            />
                        ) : value.type === 'site-external-link' ? (
                            <SiteExternalLinkActionsButton
                                upgrade={upgrade}
                                siteExternalLink={value.siteExternalLink}
                                organization={organization}
                                site={site}
                                siteStructure={siteStructure}
                                sitePermissions={sitePermissions}
                                size="small"
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </HeadlessTreeItemContent>
    );
});

/** Returns whether a site tree item is a draft. */
export function isDraftSiteTreeItem(value: AnySiteManagementItem): boolean {
    switch (value.type) {
        case 'site':
            return false;
        case 'site-space':
            return value.siteSpace.draft;
        case 'site-section':
            return value.siteSection.draft;
        case 'site-section-group':
            return value.siteSectionGroup.draft;
        case 'site-external-link':
            return value.siteExternalLink.draft;
    }
}
